import * as _ from 'lodash';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { InboundPatientService } from '../../../services/inbound-patient.service';
import { InboundPatientRecord } from '../../../types/inbound-patient-record';
import * as moment from 'moment';

@Component({
  selector: 'app-inbound-patient-home',
  templateUrl: './inbound-patient-home.component.html',
  styleUrls: ['./inbound-patient-home.component.scss']
})
export class InboundPatientHomeComponent implements OnInit, OnDestroy {

  private readonly forceLogout: boolean = true;

  inboundPatientsList: InboundPatientRecord[] = [];
  inboundPatientsListFiltered: InboundPatientRecord[] = [];
  facilities: any[] = [];
  selectedFacility: any;
  filterOption: string = 'Both';
  statusMsg: string = 'Loading...';
  isRefreshing: boolean = false;

  inBoundPatientListHeaders: any[] =  [
    { key: 'dateTime', sortKey: 'dateTimeUnix', displayValue: 'Date/Time', width: '100' },
    { key: 'sex', sortKey: 'sex', displayValue: 'Sex', width: '50' },
    { key: 'age', sortKey: 'age', displayValue: 'Age (Years)', width: '50' },
    { key: 'chiefComplaint', sortKey: 'chiefComplaint', displayValue: 'Chief Complaint', width: '' },
    { key: 'eta', sortKey: 'eta', displayValue: 'ETA (Minutes)', width: '100' },
    { key: 'bloodPressure', sortKey: 'bloodPressure', displayValue: 'Blood Pressure', width: '135' },
    { key: 'heartRate', sortKey: 'heartRate', displayValue: 'Heart Rate', width: '100' },
    { key: 'respiratoryRate', sortKey: 'respiratoryRate', displayValue: 'Respiratory Rate', width: '100' },
    { key: 'destinationFacility', sortKey: 'destinationFacility', displayValue: 'Destination Facility', width: '' },
    { key: 'medicUnit', sortKey: 'medicUnit', displayValue: 'Medic Unit #', width: '100' },
    { key: 'alertAcknowledged', sortKey: 'alertAcknowledged', displayValue: 'Acknowledge', width: '100' }
  ];
  sortKey: string = 'eta';
  sortDirection: string = 'asc';
  private subscriptions: any =  {
    inboundList: Subscription,
    isRefreshing: Subscription
  };

  constructor(private inboundPatientService: InboundPatientService) {}

  ngOnInit() {
    this.facilities = this.inboundPatientService.getUserFacilitiesList();
    this.selectedFacility = this.facilities[0];
    this.subscriptions.isRefreshing  = this.inboundPatientService.isRefreshing.subscribe(refreshing => this.isRefreshing = refreshing);
    this.getInboundPatientsList();
    this.inboundPatientService.keepSessionOpen();
  }

  ngOnDestroy() {
    this.subscriptions.inboundList.unsubscribe();
    this.subscriptions.isRefreshing.unsubscribe();
    if (this.inboundPatientService.loggedInUserHasOtherRoles()) {
      this.inboundPatientService.revertToGlobalSessionTimer(this.forceLogout);
    }
  }

  private getInboundPatientsList() {
    if (this.subscriptions.inboundList && this.subscriptions.inboundList.unsubscribe) {
      this.subscriptions.inboundList.unsubscribe();
    }
    this.subscriptions.inboundList =
      this.inboundPatientService.getInboundPatients(
        this.selectedFacility.FacilityCode
      ).subscribe((response) => {
        this.statusMsg = `${response.message || 'Last refreshed'}: ${moment().format('MM/DD HH:mm')}`;
        this.inboundPatientsList = response.data;
        this.sortInboundPatientsBy(this.sortKey, this.sortDirection, true);
      },
        (response) => {
          this.statusMsg = `${response.message ? response.message.substr(0, 50) + '...' : 'ERROR at'}: ${moment().format('MM/DD HH:mm')}`;
          this.inboundPatientsList = [];
          this.isRefreshing = false;
        });
  }

  sortInboundPatientsBy(key: string, direction: string = 'asc', forceDirection: boolean = false) {
    const sortParams: string[] = key === 'eta' ? ['eta'] : [key, 'eta'];
    let sortDirectionParams: string[];
    if (this.sortKey === key && !forceDirection) {
      direction = this.sortDirection === 'asc' ? 'desc' : 'asc';
    }
    sortDirectionParams = key === 'eta' ? [direction] : [direction, 'asc'];
    this.sortKey = key;
    this.sortDirection = direction;
    this.inboundPatientsListFiltered = this.filterList(_.orderBy(this.inboundPatientsList, sortParams, sortDirectionParams));
  }

  filterList(list: InboundPatientRecord[]) {
    switch (this.filterOption) {
      case 'Both':
        return list;
      case 'Acknowledged':
        return list.filter(p => p.alertAcknowledged);
      case 'Unacknowledged':
        return list.filter(p => !p.alertAcknowledged);
    }
  }

  acknowledgeAlert(patientRecord: InboundPatientRecord) {
    const callbacks = patientRecord.acknowledge();
    if (!callbacks) {
      return;
    }
    this.inboundPatientService.acknowledgeAlert(
      this.selectedFacility.FacilityCode, patientRecord.patientId
    ).pipe(take(1)).subscribe(
      (response) => callbacks.success(response),
      (error) => callbacks.error(error)
      );
  }

  changeFilterView(filterType: string) {
    this.filterOption = filterType || 'Both';
    this.sortInboundPatientsBy('eta', 'asc', true);
  }

  changeFacility(facilityCode: string) {
    this.selectedFacility = this.facilities.find(facility => facility.FacilityCode === facilityCode);
    this.getInboundPatientsList();
  }

}
